import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { FilterableSelect } from '@/components/ui/filterable-select'
import { Loader2 } from 'lucide-react'
import { JournalPreview } from './JournalPreview'
import { useBankAccounts, useCreateMatch } from './hooks/useReconApi'
import type { Transaction } from './types'
import { formatCurrency } from './format'

const renderCodedOption = (opt: { label: string; details?: string; code?: string }) => (
  <span className="flex items-center gap-2 min-w-0">
    {opt.code && (
      <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded shrink-0 font-mono">
        {opt.code}
      </span>
    )}
    <span className="truncate">{opt.label}</span>
    {opt.details && (
      <span className="ml-auto text-xs bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded shrink-0 tabular-nums">
        {opt.details}
      </span>
    )}
  </span>
)

interface TransferTabProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

export function TransferTab({ transaction, clientId, bankAccountId, bankAccountLabel }: TransferTabProps) {
  const { data: bankAccounts = [], isLoading } = useBankAccounts(clientId)
  const createMatch = useCreateMatch()
  const [destAccountId, setDestAccountId] = useState<number | null>(null)

  const otherAccounts = bankAccounts.filter((a) => a.id !== bankAccountId)
  const destAccount = bankAccounts.find((a) => a.id === destAccountId)
  const txnAmount = Math.abs(transaction.amount)
  const isOutgoing = transaction.amount < 0

  const destOptions = useMemo(
    () =>
      otherAccounts.map((a) => ({
        value: a.id.toString(),
        label: a.name,
        code: `${a.code}${a.sub_code ? `.${a.sub_code}` : ''}`,
        searchText: `${a.code}${a.sub_code ? `.${a.sub_code}` : ''} ${a.name}`,
      })),
    [otherAccounts]
  )

  const journalLines = destAccount
    ? [
        {
          dtLabel: isOutgoing
            ? `${destAccount.code}${destAccount.sub_code ? `.${destAccount.sub_code}` : ''} ${destAccount.name}`
            : bankAccountLabel,
          ctLabel: isOutgoing
            ? bankAccountLabel
            : `${destAccount.code}${destAccount.sub_code ? `.${destAccount.sub_code}` : ''} ${destAccount.name}`,
          amount: formatCurrency(txnAmount, transaction.currency),
        },
      ]
    : []

  const handleConfirm = () => {
    if (!destAccount) return

    createMatch.mutate({
      transaction_id: transaction.id,
      client_id: clientId,
      mirage_bank_account_id: bankAccountId,
      match_kind: 'transfer',
      counterparts: [
        {
          counterpart_kind: 'bank_account_transfer',
          dt_account_id: isOutgoing ? destAccount.id : bankAccountId,
          ct_account_id: isOutgoing ? bankAccountId : destAccount.id,
          amount: txnAmount.toFixed(4),
          note: transaction.description?.slice(0, 200) || undefined,
          doc_date: transaction.date || undefined,
        },
      ],
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-neutral-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading bank accounts...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-neutral-500">
          Destination bank / cash account
        </label>
        <FilterableSelect
          value={destAccountId?.toString() ?? ''}
          onValueChange={(v) => setDestAccountId(v ? Number(v) : null)}
          options={destOptions}
          placeholder="Select destination account..."
          searchPlaceholder="Search bank account..."
          triggerClassName="h-9 text-sm"
          renderOption={renderCodedOption}
          renderSelectedValue={renderCodedOption}
        />
      </div>

      {destAccount && <JournalPreview lines={journalLines} />}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="blue"
          disabled={!destAccount || createMatch.isLoading}
          onClick={handleConfirm}
        >
          {createMatch.isLoading ? 'Creating...' : 'Confirm transfer'}
        </Button>
        {createMatch.isError && (
          <span className="text-sm text-red-600">
            {(createMatch.error as Error).message}
          </span>
        )}
      </div>
    </div>
  )
}
